import "../../../css/CatalogSync.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Button, Tag, Spin, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import {
  triggerCatalogSync,
  getSyncJobs,
  getRunningSyncJobs,
} from "../../../services/integrations/klaviyoCatalogService";
import JobDetailDrawer from "./JobDetailDrawer";

// ── Helpers ──

const relativeTime = (dateStr) => {
  if (!dateStr) return "-";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const formatDatetime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const computeDuration = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return "-";
  const ms = new Date(completedAt) - new Date(startedAt);
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
};

const statusColor = (status) => {
  if (status === "completed") return "green";
  if (status === "failed") return "red";
  if (status === "in_progress") return "blue";
  return "default";
};

// ── Component ──

// Note: the "Synced Catalog Items" browser that used to live here (getCatalogItems)
// had no beta-API replacement when this tab was repointed (ch60569) -- its
// gandalfproxy route was already dead and nothing restored it. Removed along
// with klaviyoCatalogService's getCatalogItems/getCatalogItem.
export default function CatalogSyncTab() {
  // Status strip
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);

  // Job history
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [drawerJob, setDrawerJob] = useState(null);

  const pollRef = useRef(null);

  // ── Data fetching ──

  const fetchJobs = useCallback(async () => {
    try {
      const data = await getSyncJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent
    }
    setJobsLoading(false);
  }, []);

  const checkRunning = useCallback(async () => {
    try {
      const running = await getRunningSyncJobs();
      if (running && running.length > 0) {
        setSyncing(true);
        setSyncProgress(running[0]);
      } else {
        if (syncing) {
          // was syncing, now done
          setSyncing(false);
          setSyncProgress(null);
          fetchJobs();
        }
      }
    } catch (e) {
      // silent
    }
  }, [syncing, fetchJobs]);

  // ── Initial load ──

  useEffect(() => {
    fetchJobs();
    // Check if already syncing
    const checkInitial = async () => {
      try {
        const running = await getRunningSyncJobs();
        if (running && running.length > 0) {
          setSyncing(true);
          setSyncProgress(running[0]);
        }
      } catch (e) {
        // silent
      }
    };
    checkInitial();
  }, [fetchJobs]);

  // ── Polling ──

  useEffect(() => {
    if (syncing) {
      pollRef.current = setInterval(checkRunning, 3000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncing, checkRunning]);

  // ── Sync Now ──

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      await triggerCatalogSync();
      // Sync may be synchronous (v1), refresh everything
      setSyncing(false);
      fetchJobs();
      message.success("Catalog sync completed");
    } catch (e) {
      setSyncing(false);
      message.error("Failed to start catalog sync");
    }
  };

  // ── Latest job for status strip ──

  const latestJob = jobs.length > 0 ? jobs[0] : null;

  // ── Job history columns ──

  const jobColumns = [
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      render: (val) => formatDatetime(val),
    },
    {
      title: "Trigger",
      dataIndex: "trigger_source",
      key: "trigger_source",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{val}</Tag>,
    },
    {
      title: "Inserted",
      dataIndex: "items_inserted",
      key: "items_inserted",
    },
    {
      title: "Updated",
      dataIndex: "items_updated",
      key: "items_updated",
    },
    {
      title: "Errored",
      dataIndex: "items_errored",
      key: "items_errored",
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) => computeDuration(record.started_at, record.completed_at),
    },
  ];

  return (
    <div>
      {/* Section 1: Status Strip */}
      <div className="catalog-sync-strip">
        {syncing ? (
          <div className="catalog-sync-strip-content">
            <Spin size="small" />
            <span>
              Currently syncing...
              {syncProgress && (
                <> {syncProgress.items_inserted} inserted, {syncProgress.items_updated} updated</>
              )}
            </span>
          </div>
        ) : (
          <div className="catalog-sync-strip-content">
            <span>
              Last synced: {latestJob ? relativeTime(latestJob.completed_at || latestJob.started_at) : "never"}
            </span>
            <Button
              type="primary"
              className="primary-orange-button"
              onClick={handleSyncNow}
              icon={<SyncOutlined />}
            >
              Sync Now
            </Button>
          </div>
        )}
      </div>

      {/* Section 2: Job History */}
      <div className="catalog-sync-section">
        <div className="catalog-sync-section-title">Job History</div>
        <Table
          dataSource={jobs.slice(0, 10)}
          columns={jobColumns}
          rowKey="id"
          loading={jobsLoading}
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => setDrawerJob(record),
            style: { cursor: "pointer" },
          })}
        />
      </div>

      {/* Job Detail Drawer */}
      <JobDetailDrawer
        job={drawerJob}
        open={!!drawerJob}
        onClose={() => setDrawerJob(null)}
      />
    </div>
  );
}
